/**
 * CAT Scale weigh-ticket CSVs uploaded through the "Create from Document" dialog.
 *
 * The server recognises the format and returns line items already shaped like
 * tbl_ExpenseLine (POST excel/preview — see App/Controllers/Documents/CONTEXT.md in the
 * backend repo). This module holds the rules that survive that hand-off: how a preview
 * item becomes an ordinary bill line, and which of those lines still need a human.
 *
 * A scale line is a bill line — description, account, amount, charge to — so it carries no
 * import-specific controls. Who the line is charged to follows the company's pay-to
 * setting (the preview's PayTo): the ticket's driver for driver-paid companies, the truck
 * matched from its Truck Number for truck-paid ones. Where the server could not match that
 * payer, the line is flagged and the user fills the charge-to in themselves.
 *
 * Deliberately free of app imports — like vendor-match.js and item-match-helpers.js
 * beside it. Building Field instances would mean importing the form module, which drags
 * in the util-constants/localStorage import cycle and makes the file untestable. So this
 * returns plain rows and plain patches; the dialog does the Field plumbing.
 */

export const FORMAT_CAT_SCALE = 'CAT_SCALE';

/** The preview's PayTo. Mirrors CatScaleImportManager::PAY_TO_*. */
export const PAY_TO_DRIVER = 1;
export const PAY_TO_TRUCK = 2;

/**
 * Warning codes a row can carry: which payer the server could not resolve, and why.
 * The server's cell errors (NOT_FOUND / AMBIGUOUS, from resolveDriver / resolveTruck) are
 * prefixed with the payer kind so one key tells the UI what to say.
 */
export const DRIVER_NOT_FOUND = 'DRIVER_NOT_FOUND';
export const DRIVER_AMBIGUOUS = 'DRIVER_AMBIGUOUS';
export const TRUCK_NOT_FOUND = 'TRUCK_NOT_FOUND';
export const TRUCK_AMBIGUOUS = 'TRUCK_AMBIGUOUS';

/** Translation key for each warning code; takes the name/number off the ticket as {0}. */
export const MATCH_WARNING_TEXT = {
    [DRIVER_NOT_FOUND]: 'text.driver_not_found',
    [DRIVER_AMBIGUOUS]: 'text.driver_ambiguous',
    [TRUCK_NOT_FOUND]: 'text.truck_not_found',
    [TRUCK_AMBIGUOUS]: 'text.truck_ambiguous',
};

const isAmbiguous = (code) => code === DRIVER_AMBIGUOUS || code === TRUCK_AMBIGUOUS;

export function isCsvFile(file) {
    if (!file) {
        return false;
    }

    const type = String(file.type ?? '').toLowerCase();
    if (type === 'text/csv' || type === 'application/csv') {
        return true;
    }

    // Windows registers .csv as application/vnd.ms-excel when Excel is installed, and some
    // browsers hand over an empty type, so the extension is the reliable discriminator.
    return String(file.name ?? '').toLowerCase().endsWith('.csv');
}

/**
 * The two accounts a scale line can land in, as {value, label} ready for a select-search.
 * Both come from the company's accounting settings, which is also what the dialog sends
 * to the server as ExpensesAccountID / AdvancesAccountID.
 */
export function getPreviewAccounts(defaults) {
    return {
        expenses: {value: defaults?.ExpenseAccountID ?? null, label: defaults?.ExpenseAccount ?? ''},
        advances: {value: defaults?.AdvancesAccountID ?? null, label: defaults?.AdvancesAccount ?? ''},
    };
}

/** Label for an AccountID the server already chose, so the select renders filled in. */
export function accountLabelFor(accountID, defaults) {
    const accounts = getPreviewAccounts(defaults);

    if (accountID && accounts.advances.value && Number(accountID) === Number(accounts.advances.value)) {
        return accounts.advances.label;
    }
    if (accountID && accounts.expenses.value && Number(accountID) === Number(accounts.expenses.value)) {
        return accounts.expenses.label;
    }

    return '';
}

/**
 * Preview items -> flat rows for getExpenseItemField. The mirror of itemsFromExtraction's
 * input on the AI path; unlike that one it needs no Qty/Amount repair, because the server
 * sends one ticket per line with Qty 1 and a real Fee.
 */
export function catScaleRowsFromPreview(previewItems, defaults, payTo = PAY_TO_DRIVER) {
    const isTruckPay = Number(payTo) === PAY_TO_TRUCK;

    return (previewItems ?? []).map((it) => {
        const amount = Number(it.Amount) || 0;
        // Only the payer's cell can flag a line: a truck-paid company does not need the
        // driver matched, and vice versa.
        const payerCell = isTruckPay ? it.Truck : it.Driver;
        const payerError = payerCell?.error ?? '';

        return {
            Description: it.Description ?? '',
            AccountID: it.AccountID ?? null,
            Account: accountLabelFor(it.AccountID, defaults),
            Amount: amount,
            Qty: Number(it.Qty) || 1,
            TotalAmount: Number(it.TotalAmount) || amount,

            // No ChargeTo discriminator here on purpose: getExpenseItemField derives it from
            // the FKs below (chargeToFromItem), so there is one source of truth. The server
            // sets a contact / truck only for a resolved payer who pays their own fuel;
            // everything else stays on the company.
            ChargeToContactID: it.ChargeToContactID ?? '',
            ChargeToTruckID: it.ChargeToTruckID ?? '',
            // Paired labels for fillFieldsFromData, so the Charge To select renders filled in.
            ChargeToContact: it.ChargeToContactID ? (it.ChargeToContact ?? '') : '',
            ChargeToTruck: it.ChargeToTruckID ? (it.ChargeToTruck ?? '') : '',

            // Not columns — the payer's name/number off the ticket and why it could not be
            // matched, kept so the warning can say whose line needs attention.
            MatchName: payerCell?.name ?? (payerError ? (payerCell?.value ?? '') : ''),
            MatchWarning: payerError ? `${isTruckPay ? 'TRUCK' : 'DRIVER'}_${payerError}` : '',
        };
    });
}

/**
 * Banner data: how many lines still need a payer, which names/numbers to show, and whether
 * those are trucks (picks the banner text). Reads the dialog's Field maps defensively so
 * it works on a live, partly-fixed list.
 */
export function catScaleWarningSummary(items) {
    const unresolved = (items ?? []).filter((it) => !!it?.MatchWarning?.value);

    return {
        count: unresolved.length,
        names: Array.from(new Set(unresolved.map((it) => it?.MatchName?.value).filter(Boolean))),
        hasAmbiguous: unresolved.some((it) => isAmbiguous(it.MatchWarning.value)),
        isTruck: unresolved.some((it) => String(it.MatchWarning.value).startsWith('TRUCK_')),
    };
}

/** Tooltip text for one row's warning, or '' when the row has none. */
export function matchWarningText(row, translate) {
    const key = MATCH_WARNING_TEXT[row?.MatchWarning];

    return key ? translate(key, [row.MatchName]) : '';
}
